// Payment Success screen with EMAIL and CHECKBOXES to send receipt and newsletter.
// After Continuar or sending receipt: show confirmation only — no redirect to admin/login.

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Mail, Star, CheckCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRestaurant } from '../../../providers/RestaurantContext';

const PaymentSuccessOptionsScreen: React.FC = () => {
  const location = useLocation();
  const { transactionId, restaurantId, amount, mesaId, restaurantName, serverName, staffId } = location.state || {};
  const { restaurant } = useRestaurant();
  const resolvedRestaurantId = restaurantId || restaurant?.id;
  
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [email, setEmail] = useState('');
  const [wantsNews, setWantsNews] = useState(false);
  const [wantsFiscal, setWantsFiscal] = useState(false);
  const [rnc, setRnc] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);

  const handleSendReceipt = async () => {
    if (!resolvedRestaurantId) {
      alert('Error: No se encontró información del restaurante');
      return;
    }

    setSendingReceipt(true);

    try {
      const functions = getFunctions();
      const confirmPayment = httpsCallable(functions, 'confirmPayment');

      await confirmPayment({
        restaurantId: resolvedRestaurantId,
        mesaId: String(mesaId),
        selectedItemIds: [],
        subtotal: Number(amount || 0),
        email: email
      });
      
      alert('¡Recibo enviado exitosamente!');
    } catch (error: any) {
      console.error('Error sending receipt:', error);
      alert('Error al enviar el recibo. Por favor intenta de nuevo.');
    } finally {
      setSendingReceipt(false);
      setShowReceiptModal(false);
      setShowCompletionMessage(true);
    }
  };

  const handleContinue = () => {
    setShowCompletionMessage(true);
  };

  // Final confirmation: no redirects, customer can close the page
  if (showCompletionMessage) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Pago completado correctamente.
            </h1>
            <p className="text-gray-600">
              Puedes cerrar esta página o volver al restaurante.
            </p>
            <p className="text-gray-600 mt-2">
              Gracias por usar YAP.
            </p>
          </div>
          <p className="text-gray-500 text-sm">
            Próximamente podrás descargar la app YAP para pagar aún más rápido.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold">Gracias!</h1>
        <button onClick={() => setShowCompletionMessage(true)} className="w-8 h-8 flex items-center justify-center" aria-label="Cerrar">
          <X className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      <div className="max-w-md mx-auto px-4 py-8">
        {/* Success Message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-green-900">¡Tu pago fue exitoso!</p>
              <p className="text-xs text-green-700 mt-1">
                El servidor reconoce que la cuenta fue pagada, favor seleccionar una de las opciones de abajo.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => setShowReceiptModal(true)}
            className="w-full bg-white border-2 border-gray-300 text-gray-900 py-4 rounded-xl text-base font-semibold hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
          >
            <Mail className="w-5 h-5" />
            Recibir recibo por correo electrónico
          </button>

          <button
            onClick={handleContinue}
            className="w-full bg-black text-white py-4 rounded-xl text-base font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <Star className="w-5 h-5" />
            Continuar
          </button>
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
          <div className="bg-white w-full h-[85vh] rounded-t-3xl overflow-y-auto animate-slide-up">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center">
              <button onClick={() => setShowReceiptModal(false)} className="mr-4">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-lg font-semibold flex-1">Recibir recibo</h2>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5">
              <p className="text-sm text-gray-600">
                Llena los campos de abajo para recibir tu recibo
              </p>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-base min-h-[44px]"
                />
              </div>

              {/* Newsletter Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantsNews}
                  onChange={(e) => setWantsNews(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">
                  Deseo recibir las últimas noticias sobre <span className="font-semibold">{restaurantName || 'este restaurante'}</span>
                </span>
              </label>

              {/* Fiscal Receipt Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantsFiscal}
                  onChange={(e) => setWantsFiscal(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700 font-medium">
                  Comprobante fiscal
                </span>
              </label>

              {/* RNC Input (conditional) */}
              {wantsFiscal && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">RNC</label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={rnc}
                    onChange={(e) => setRnc(e.target.value)}
                    placeholder="000-00000-0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-base min-h-[44px]"
                  />
                </div>
              )}

              {/* Send Button */}
              <button
                onClick={handleSendReceipt}
                disabled={!email || sendingReceipt}
                className="w-full bg-black text-white py-4 rounded-xl font-semibold hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
              >
                {sendingReceipt ? 'Enviando...' : 'Enviar recibo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccessOptionsScreen;
